<template>
  <Teleport to="body">
    <div
      v-show="visible"
      ref="tipRef"
      class="spd-hover-tooltip"
      role="tooltip"
      :style="tipStyle"
    >
      {{ text }}
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const SHOW_DELAY_MS = 380
const GAP_PX = 8

const visible = ref(false)
const text = ref('')
const tipStyle = ref<Record<string, string>>({})
const tipRef = ref<HTMLElement | null>(null)

let showTimer: ReturnType<typeof setTimeout> | null = null
let host: HTMLElement | null = null
let savedTitle = ''

function findTitleHost(start: EventTarget | null): HTMLElement | null {
  if (!(start instanceof Element)) return null
  const el = start.closest('[title]')
  if (!(el instanceof HTMLElement)) return null
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return null
  if (!el.getAttribute('title')?.trim()) return null
  return el
}

function clearShowTimer() {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
}

function restoreTitle() {
  if (host && savedTitle) {
    host.setAttribute('title', savedTitle)
  }
  host = null
  savedTitle = ''
}

function hide() {
  clearShowTimer()
  visible.value = false
  text.value = ''
  restoreTitle()
}

function place() {
  const tip = tipRef.value
  if (!tip || !host) return
  const rect = host.getBoundingClientRect()
  const tipW = tip.offsetWidth
  const tipH = tip.offsetHeight
  let left = rect.left + rect.width / 2 - tipW / 2
  let top = rect.top - tipH - GAP_PX
  if (top < GAP_PX) top = rect.bottom + GAP_PX
  left = Math.min(Math.max(GAP_PX, left), window.innerWidth - tipW - GAP_PX)
  tipStyle.value = {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
  }
}

async function showFor(el: HTMLElement) {
  savedTitle = el.getAttribute('title')?.trim() || ''
  if (!savedTitle) return
  host = el
  el.removeAttribute('title')
  text.value = savedTitle
  visible.value = true
  tipStyle.value = { left: '-9999px', top: '0' }
  await nextTick()
  place()
}

function onMouseOver(event: MouseEvent) {
  const target = event.target
  if (host && target instanceof Node && host.contains(target)) return
  const next = findTitleHost(target)
  if (!next) return
  if (next === host) return
  const from = findTitleHost(event.relatedTarget)
  if (from === next) return
  hide()
  showTimer = setTimeout(() => {
    void showFor(next)
  }, SHOW_DELAY_MS)
}

function onMouseOut(event: MouseEvent) {
  const related = event.relatedTarget
  if (host) {
    if (related instanceof Node && host.contains(related)) return
    hide()
    return
  }
  const leaving = findTitleHost(event.target)
  if (!leaving) return
  if (related instanceof Node && leaving.contains(related)) return
  hide()
}

function onScroll() {
  if (visible.value) hide()
}

onMounted(() => {
  document.addEventListener('mouseover', onMouseOver, true)
  document.addEventListener('mouseout', onMouseOut, true)
  window.addEventListener('scroll', onScroll, true)
})

onBeforeUnmount(() => {
  hide()
  document.removeEventListener('mouseover', onMouseOver, true)
  document.removeEventListener('mouseout', onMouseOut, true)
  window.removeEventListener('scroll', onScroll, true)
})
</script>
